import React from "react";
import { Eye, FileSearch } from "lucide-react";
import Loader from "@/components/Loader";

const columns = [
  { key: "name", label: "Campaign Name" },
  { key: "recipients_count", label: "Total Candidates" },
  { key: "total_sent", label: "Total Sent" },
  { key: "channel", label: "Channel" },
  { key: "sent_at", label: "Sent At" },
  { key: "view", label: "View" },
];

const formatSentAt = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return `${date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })} ${date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const CountButton = ({ count, onClick }) => {
  if (!count) {
    return <span className="text-sm font-normal text-foreground">0</span>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-sm font-normal text-green-600 underline-offset-2 hover:text-green-500 hover:underline"
    >
      {count}
    </button>
  );
};

const MarketingHistoryTable = ({
  rows,
  isLoading,
  searchTerm = "",
  channelLabels = {},
  onCountClick,
  onViewClick,
}) => {
  const renderCell = (column, row) => {
    switch (column.key) {
      case "name":
        return (
          <div className="text-sm font-semibold text-foreground">
            {row.name || "-"}
          </div>
        );
      case "recipients_count":
        return (
          <div className="text-sm font-normal text-green-600">
            {row.recipients_count ?? 0}
          </div>
        );
      case "total_sent":
        return (
          <CountButton
            count={row.total_sent ?? 0}
            onClick={() => onCountClick?.(row)}
          />
        );
      case "channel":
        return (
          <div className="text-sm font-normal text-foreground">
            {channelLabels[row.channel] || row.channel || "-"}
          </div>
        );
      case "sent_at":
        return (
          <div className="text-sm font-normal text-foreground">
            {formatSentAt(row.sent_at)}
          </div>
        );
      case "view":
        return (
          <button
            type="button"
            onClick={() => onViewClick?.(row)}
            className="inline-flex items-center justify-center rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-primary-dark focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label={`View details for ${row.name || "campaign"}`}
            title="View campaign details"
          >
            <Eye className="h-4 w-4" aria-hidden="true" />
          </button>
        );
      default:
        return null;
    }
  };

  const getCellClass = () => "px-4 py-4 whitespace-nowrap";

  if (!isLoading && !rows.length) {
    return (
      <div className="text-center py-4 text-red-600 bg-red-200 rounded-lg">
        <div className="mb-2">
          <FileSearch className="h-12 w-12 mx-auto" />
        </div>
        <p className="text-sm">
          No campaign history found
          {searchTerm.trim() ? " for your search." : "."}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto bg-card rounded-lg border border-border shadow">
      <div className="hidden md:block">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted">
            <tr className="border-b border-border">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="px-4 pt-4 pb-2 text-foreground align-top relative"
                >
                  <div className="mb-[10px] flex items-center">
                    <p className="mr-1 text-base font-medium text-muted-foreground whitespace-nowrap">
                      {column.label}
                    </p>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="py-10">
                  <div className="flex justify-center items-center">
                    <Loader bgColor="bg-transparent" />
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="hover:bg-muted">
                  {columns.map((column) => (
                    <td key={column.key} className={getCellClass(column)}>
                      {renderCell(column, row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="md:hidden divide-y divide-border">
        {isLoading ? (
          <div className="py-10">
            <div className="flex justify-center items-center">
              <Loader bgColor="bg-transparent" />
            </div>
          </div>
        ) : (
          rows.map((row) => (
            <article key={row.id} className="px-4 py-4 bg-card">
              <div className="flex items-start gap-3 pb-3 mb-3 border-b border-border">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-foreground">
                    {row.name || "-"}
                  </div>
                </div>
              </div>
              <div className="space-y-2.5">
                <div className="flex gap-3 items-start">
                  <span className="text-xs font-medium text-muted-foreground w-[40%] shrink-0 leading-snug pt-0.5">
                    Total Candidates
                  </span>
                  <div className="text-sm font-normal text-green-600 flex-1 text-right min-w-0">
                    {row.recipients_count ?? 0}
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <span className="text-xs font-medium text-muted-foreground w-[40%] shrink-0 leading-snug pt-0.5">
                    Total Sent
                  </span>
                  <div className="flex-1 text-right min-w-0">
                    <CountButton
                      count={row.total_sent ?? 0}
                      onClick={() => onCountClick?.(row)}
                    />
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <span className="text-xs font-medium text-muted-foreground w-[40%] shrink-0 leading-snug pt-0.5">
                    Channel
                  </span>
                  <div className="text-sm font-normal text-foreground flex-1 text-right min-w-0">
                    {channelLabels[row.channel] || row.channel || "-"}
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <span className="text-xs font-medium text-muted-foreground w-[40%] shrink-0 leading-snug pt-0.5">
                    Sent At
                  </span>
                  <div className="text-sm font-normal text-foreground flex-1 text-right min-w-0">
                    {formatSentAt(row.sent_at)}
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <span className="text-xs font-medium text-muted-foreground w-[40%] shrink-0 leading-snug pt-0.5">
                    View
                  </span>
                  <div className="flex-1 text-right min-w-0">
                    {renderCell({ key: "view" }, row)}
                  </div>
                </div>
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
};

export default MarketingHistoryTable;
